/** Preview models use the Core schema; anonymous payloads pass through the same strict adapter. */
export type DocumentPreview = CoreAPI.AssetDocumentPreviewVO;
export type DocumentPreviewOptions = Omit<
CoreAPI.AssetDocumentPreviewRequest,
'mediaId'
>;
export type DocumentPreviewLoader = (
  options: DocumentPreviewOptions,
  signal: AbortSignal,
) => Promise<DocumentPreview>;

export const isPreviewRecord = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const validTime = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value));
const index = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const content = (value: unknown): value is string => typeof value === 'string';
const invalid = (): never => {
  throw new Error('预览数据不完整，请重新打开');
};
function record(value: unknown) {
  if (!isPreviewRecord(value)) return invalid();
  return value;
}
function rows(value: unknown, max: number): unknown[] {
  if (!Array.isArray(value) || value.length > max) return invalid();
  return value;
}
function cursor(offset: unknown, next: unknown, more: unknown) {
  if (
    !index(offset) ||
    typeof more !== 'boolean' ||
    (more && (!index(next) || next <= offset))
  ) invalid();
}

export function parsePreviewFileAccess(
  value: unknown,
  deadline?: string,
  allowStablePublic = false,
): CoreAPI.AssetFileAccessVO {
  const item = record(value);
  if (
    !content(item.url) ||
    (!validTime(item.expiresAt) &&
      !(allowStablePublic && item.expiresAt == null)) ||
    !validTime(item.serverTime) ||
    !content(item.disposition)
  ) return invalid();
  let url: URL;
  try {
    url = new URL(item.url);
  } catch {
    return invalid();
  }
  const expiresAt = validTime(item.expiresAt) ? item.expiresAt : undefined;
  const duration = expiresAt
    ? Date.parse(expiresAt) - Date.parse(item.serverTime)
    : 1;
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    duration <= 0 ||
    duration > 60000 ||
    (deadline && expiresAt && Date.parse(expiresAt) > Date.parse(deadline))
  ) return invalid();
  return {
    url: item.url,
    expiresAt,
    serverTime: item.serverTime,
    disposition: item.disposition,
  };
}

export function parseDocumentPreview(
  value: unknown,
  deadline?: string,
  allowStablePublic = false,
): DocumentPreview {
  const item = record(value);
  if (
    !validTime(item.serverTime) ||
    !validTime(item.expiresAt) ||
    Date.parse(item.expiresAt) <= Date.parse(item.serverTime) ||
    Date.parse(item.expiresAt) - Date.parse(item.serverTime) > 60000 ||
    (deadline && Date.parse(item.expiresAt) > Date.parse(deadline))
  ) return invalid();
  const warnings: CoreAPI.AssetDocumentPreviewWarning[] = rows(
    item.warnings,
    20,
  ).map((valueValue) => {
    const warning = record(valueValue);
    if (!content(warning.code) || !content(warning.message)) return invalid();
    const { code } = warning;
    if (
      code !== 'LAYOUT_SIMPLIFIED' &&
      code !== 'CONTENT_TRUNCATED' &&
      code !== 'CELL_VALUE_TRUNCATED' &&
      code !== 'FORMULA_CACHE_MISSING' &&
      code !== 'ENCODING_FALLBACK'
    ) return invalid();
    return { code, message: warning.message };
  });
  const base = {
    serverTime: item.serverTime,
    expiresAt: item.expiresAt,
    warnings,
  };
  const branches = [
    'text',
    'document',
    'fileAccess',
    'workbook',
    'slide',
  ].filter((key) => item[key] != null);
  if (branches.length !== 1) return invalid();
  if (item.kind === 'TEXT') {
    const text = record(item.text);
    cursor(text.offset, text.nextOffset, text.hasMore);
    if (
      !content(text.content) ||
      text.content.length > 65536 ||
      !content(text.encoding) ||
      !index(text.offset) ||
      typeof text.hasMore !== 'boolean' ||
      (text.hasMore && !text.content.length)
    ) return invalid();
    return {
      ...base,
      kind: 'TEXT',
      text: {
        content: text.content,
        offset: text.offset,
        nextOffset: index(text.nextOffset) ? text.nextOffset : undefined,
        hasMore: text.hasMore,
        encoding: text.encoding,
      },
    };
  }
  if (item.kind === 'DOCUMENT') {
    const document = record(item.document);
    cursor(document.offset, document.nextOffset, document.hasMore);
    if (!index(document.offset) || typeof document.hasMore !== 'boolean') return invalid();
    const blocks: CoreAPI.AssetDocumentPreviewBlock[] = rows(
      document.blocks,
      200,
    ).map((valueValue) => {
      const block = record(valueValue);
      if (block.type === 'PARAGRAPH' && content(block.text)) return { type: 'PARAGRAPH', text: block.text };
      if (
        block.type === 'TABLE_ROW' &&
        index(block.tableIndex) &&
        index(block.rowIndex)
      ) {
        const cells = rows(block.cells, 1000).map((cellValue) =>
          (content(cellValue) ? cellValue : invalid()));
        return {
          type: 'TABLE_ROW',
          tableIndex: block.tableIndex,
          rowIndex: block.rowIndex,
          cells,
        };
      }
      return invalid();
    });
    if (document.hasMore && !blocks.length) return invalid();
    return {
      ...base,
      kind: 'DOCUMENT',
      document: {
        blocks,
        offset: document.offset,
        nextOffset: index(document.nextOffset)
          ? document.nextOffset
          : undefined,
        hasMore: document.hasMore,
      },
    };
  }
  if (item.kind === 'DOCX') {
    return {
      ...base,
      kind: 'DOCX',
      fileAccess: parsePreviewFileAccess(
        item.fileAccess,
        item.expiresAt,
        allowStablePublic,
      ),
    };
  }
  if (item.kind === 'WORKBOOK') {
    const workbook = record(item.workbook);
    cursor(workbook.rowOffset, workbook.nextRowOffset, workbook.hasMoreRows);
    cursor(
      workbook.columnOffset,
      workbook.nextColumnOffset,
      workbook.hasMoreColumns,
    );
    if (
      !index(workbook.sheetIndex) ||
      !index(workbook.rowOffset) ||
      !index(workbook.columnOffset) ||
      typeof workbook.hasMoreRows !== 'boolean' ||
      typeof workbook.hasMoreColumns !== 'boolean'
    ) return invalid();
    const sheets = rows(workbook.sheets, 1024).map((valueValue) => {
      const sheet = record(valueValue);
      return index(sheet.index) && content(sheet.name)
        ? { index: sheet.index, name: sheet.name }
        : invalid();
    });
    if (!sheets.some((sheet) => sheet.index === workbook.sheetIndex)) return invalid();
    const data = rows(workbook.rows, 100).map((valueValue) => {
      const row = record(valueValue);
      if (!index(row.rowIndex)) return invalid();
      const cells = rows(row.cells, 50).map((cellValue) => {
        const cell = record(cellValue);
        return index(cell.columnIndex) &&
          content(cell.text) &&
          typeof cell.truncated === 'boolean'
          ? {
            columnIndex: cell.columnIndex,
            text: cell.text,
            truncated: cell.truncated,
          }
          : invalid();
      });
      return { rowIndex: row.rowIndex, cells };
    });
    if ((workbook.hasMoreRows || workbook.hasMoreColumns) && !data.length) return invalid();
    return {
      ...base,
      kind: 'WORKBOOK',
      workbook: {
        sheets,
        sheetIndex: workbook.sheetIndex,
        rowOffset: workbook.rowOffset,
        columnOffset: workbook.columnOffset,
        rows: data,
        nextRowOffset: index(workbook.nextRowOffset)
          ? workbook.nextRowOffset
          : undefined,
        nextColumnOffset: index(workbook.nextColumnOffset)
          ? workbook.nextColumnOffset
          : undefined,
        hasMoreRows: workbook.hasMoreRows,
        hasMoreColumns: workbook.hasMoreColumns,
      },
    };
  }
  if (item.kind === 'SLIDES') {
    const slide = record(item.slide);
    if (
      !index(slide.slideIndex) ||
      !index(slide.slideCount) ||
      slide.slideIndex >= slide.slideCount ||
      !index(slide.width) ||
      !index(slide.height) ||
      slide.width === 0 ||
      slide.height === 0 ||
      slide.width > 1600 ||
      slide.height > 1600 ||
      slide.width * slide.height > 2000000 ||
      slide.mimeType !== 'image/png' ||
      !content(slide.imageBase64) ||
      slide.imageBase64.length > 2796204 ||
      !/^iVBORw0KGgo[A-Za-z0-9+/]*={0,2}$/.test(slide.imageBase64)
    ) return invalid();
    return {
      ...base,
      kind: 'SLIDES',
      slide: {
        slideIndex: slide.slideIndex,
        slideCount: slide.slideCount,
        width: slide.width,
        height: slide.height,
        mimeType: 'image/png',
        imageBase64: slide.imageBase64,
      },
    };
  }
  return invalid();
}
